import React, { useCallback, useState } from 'react';
import { P2TextDescription, P2TextLabel, P2Modal } from 'react-bootstrap-common-script';
import ClockPendingTask from '../../../assets/svg/clock-pending-task';
import { DocumentItem, FileItem } from '../../../models/polymer/Document.model';
import { Helpers } from '../../../utils/helper';
import { reduce } from 'lodash';
import FileSelectionSegment from '../segments/file-selection-segment';
import { StageDReviewApplication, StageDocumentsInfo } from '../../../models/classes/stage-d/Review-Application.class';
import he from 'he';
interface DocumentationListsProps {
  document_list: DocumentItem[];
  setFileItemsStorage?: Function;
  filesItemsStorage: FileItem[];
  setParentFormValid?: React.Dispatch<React.SetStateAction<boolean>>;
  isFirstSection: boolean;
  folders: StageDocumentsInfo[];
  sectionTitleType?: 'text' | 'badge';
  applicationReviewSetFormValue: React.Dispatch<React.SetStateAction<StageDReviewApplication>>;
}

function Documents({
  document_list = [],
  filesItemsStorage = [],
  setFileItemsStorage,
  isFirstSection,
  folders,
  sectionTitleType = 'text',
}: DocumentationListsProps) {
  const [additionalCheckbox, setAdditionalCheckbox] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messageSize, setMessageSize] = useState<'5MB' | '10MB'>('10MB');

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  const handleModalTrigger = (type: '5MB' | '10MB') => {
    setMessageSize(type);
    setIsModalOpen(true);
  };

  const checkboxClick = useCallback(
    (document_code: string) => {
      let existing_document = additionalCheckbox;
      let found_index = existing_document.findIndex((e) => e === document_code);
      if (found_index === -1) {
        existing_document.push(document_code);
      } else {
        existing_document.splice(found_index, 1);
      }
      setAdditionalCheckbox([...existing_document]);
    },
    [additionalCheckbox]
  );

  const checkSelectedFilesSize = useCallback(
    (files: FileList | null, code: string) => {
      let storageFiles = filesItemsStorage.filter((e) => e.code === code);

      let totalSize = reduce(
        storageFiles.map((e) => e.file_content.size),
        (sum, n: any) => {
          return sum + n;
        },
        0
      );

      const fileLength = files?.length ?? 0;
      if (files) {
        for (let i = 0; i < fileLength; i++) {
          let fileContent: File = files[i];
          totalSize += fileContent.size;
        }
      }

      return Helpers.isBytesExceed(totalSize, 10000000);
    },
    [filesItemsStorage]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, code: string, label: string, file?: File) => {
      let existingFiles = filesItemsStorage;

      const {
        target: { files },
      } = event;

      const fileLength = files?.length ?? 0;

      if (!checkSelectedFilesSize(files, code) && files) {
        for (let i = 0; i < fileLength; i++) {
          let fileContent: File = files[i];

          if (Helpers.isBytesExceed(fileContent.size, 5000000)) {
            handleModalTrigger('5MB');
          } else {
            let fileItemObj = new FileItem({ code: code, label: label, file: file });
            fileItemObj.setFileInfo(fileContent);

            const reader = new FileReader();
            reader.readAsDataURL(fileContent);
            reader.onload = (e) => {
              const { result } = reader;
              let base64 = Helpers.stripBase64(result?.toString()) ?? '';
              fileItemObj.setFileBase64(base64);
            };

            existingFiles.push(fileItemObj);
          }
        }
        setFileItemsStorage?.([...existingFiles]);
      } else {
        handleModalTrigger('10MB');
      }
    },
    [filesItemsStorage]
  );

  const clearFileChange = useCallback(
    (id: string) => {
      let existingFiles = filesItemsStorage;
      let index = existingFiles.findIndex((e) => e.file_content.id === id);
      if (index !== -1) {
        existingFiles.splice(index, 1);
      }
      setFileItemsStorage?.([...existingFiles]);
    },
    [filesItemsStorage]
  );

  const assignFileFields = useCallback(
    (file_id: string, submitted_date: string, key: string, id: string) => {
      let existingFiles = filesItemsStorage;
      let index = existingFiles.findIndex((e) => e.file_content.id === id);
      if (index !== -1) {
        let newObj = new FileItem(existingFiles[index]);
        newObj.setFileId(file_id);
        newObj.setSubmittedDate(submitted_date);
        newObj.setKey(key);
        existingFiles[index] = newObj;
      }
      setFileItemsStorage?.([...existingFiles]);
    },
    [filesItemsStorage]
  );

  const assignError = useCallback(
    (isError: boolean, id: string) => {
      let existingFiles = [...filesItemsStorage.map((e) => new FileItem(e))];
      let index = existingFiles.findIndex((e) => e.file_content.id === id);
      if (index !== -1) {
        existingFiles[index].setIsError(isError);
      }
      setFileItemsStorage?.(existingFiles);
    },
    [filesItemsStorage]
  );

  const modalMessage =
    messageSize === '10MB'
      ? `You have now exceeded the maximum of 10MB worth of files. Please select another file or reduce the file size.`
      : `You have exceeded the maximum file size of 5MB. Please select another file or reduce the file size.`;

  return (
    <div className='polymer-stage-d-container__section' style={{ paddingTop: isFirstSection ? 0 : 24 }}>
      <div className='polymer-stage-d-container__title'>
        {sectionTitleType === 'badge' ? (
          <div className='polymer-stage-d-container__title_badge'>Document</div>
        ) : (
          <P2TextDescription color='#000' title='Documents' size='1.5rem' />
        )}
      </div>

      <div>
        {/* 3 Sept 2025 - Change documents component to be a simple component that display ALL documents parsed to it. Logic to filter should be handled in parent component */}
        {folders.map((item, _index) => {
          //Create custom Document with desired code and label based on the STAGE D structure
          let customDocument = new DocumentItem({
            label: item.name,
            code: item.uuid,
            description: '',
            checkbox: false,
            checkbox_label: '',
            sample: '',
          });

          const files = filesItemsStorage.filter((file) => file.label === item.name && file.code === item.uuid);
          const downloadKey = item.is_request_document ? '' : item.comments?.[0].documents?.[0]?.key;
          return (
            <>
              <div
                key={item.uuid}
                style={{
                  borderBottom: _index !== folders?.length - 1 ? '1px dashed #0C8188' : '',
                  paddingTop: _index > 0 ? '16px' : 0,
                }}>
                <div className='polymer-stage-d-container__subtitle'>
                  <P2TextLabel bold={700} marginBottom='37px' label={he.decode(item.name ?? '')} />
                  <div className='polymer-stage-d-container__subtitle__date'>
                    <ClockPendingTask />
                    <p>Received on {Helpers.formatDate(item.received_date)}</p>
                  </div>
                </div>

                <FileSelectionSegment
                  comment={item.comments}
                  index={_index}
                  document={customDocument}
                  folderName={item.name}
                  files={files}
                  clearFileChange={clearFileChange}
                  handleFileChange={handleFileChange}
                  assignFileFields={assignFileFields}
                  assignError={assignError}
                  additionalCheckbox={additionalCheckbox}
                  additionalCheckboxOnClick={checkboxClick}
                  require_label={false}
                  disabled={!item.is_request_document}
                  download_only={!item.is_request_document}
                  download_key={downloadKey}
                />
              </div>
            </>
          );
        })}
      </div>
      <div className='polymer-small-width'>
        <P2Modal
          isOpen={isModalOpen}
          onCloseModal={closeModal}
          variant='close-button'
          title='Maximum file size limit exceeded'
          children={<p>{modalMessage}</p>}
        />
      </div>
    </div>
  );
}

export default Documents;
